/**
 * Custom Branded Telegram Login Button
 *
 * Overlay approach: real Telegram widget is transparent on top,
 * our branded button is visible underneath. Click goes to real widget.
 * Styled to match ChooseRolePage (emerald gradient, rounded-xl).
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { IconTelegram } from "./Icons.tsx";
import type { TelegramWidgetData } from "../lib/auth.tsx";

interface TelegramLoginButtonProps {
  botName: string;
  onAuth: (data: TelegramWidgetData) => void;
}

declare global {
  interface Window {
    TelegramLoginWidgetCallback?: (data: TelegramWidgetData) => void;
  }
}

export function TelegramLoginButton({ botName, onAuth }: TelegramLoginButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  const handleAuth = useCallback(
    (data: TelegramWidgetData) => {
      onAuth(data);
    },
    [onAuth]
  );

  useEffect(() => {
    window.TelegramLoginWidgetCallback = handleAuth;

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", botName);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-radius", "12");
    script.setAttribute("data-onauth", "TelegramLoginWidgetCallback(user)");
    script.setAttribute("data-request-access", "write");
    script.setAttribute("data-userpic", "false");

    script.onload = () => {
      setTimeout(() => setIsReady(true), 200);
    };

    const container = containerRef.current;
    if (container) {
      // Clear container safely without innerHTML (XSS prevention)
      container.replaceChildren();
      container.appendChild(script);
    }

    return () => {
      delete window.TelegramLoginWidgetCallback;
      if (container) {
        // Clear container safely without innerHTML
        container.replaceChildren();
      }
    };
  }, [botName, handleAuth]);

  return (
    <div className="relative w-full min-h-[48px]">
      {/* Our branded button (visible, underneath) */}
      <div
        className={`flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] ${
          !isReady ? "opacity-50" : ""
        }`}
        style={{
          background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
        }}
      >
        <IconTelegram className="w-5 h-5 xs:w-6 xs:h-6" />
        <span>Войти через Telegram</span>
      </div>

      {/* Real Telegram widget (transparent, on top, catches clicks) */}
      <div
        ref={containerRef}
        className="telegram-login-button-container absolute inset-0 flex items-center justify-center cursor-pointer overflow-hidden"
      />

      <style>{`
        .telegram-login-button-container iframe {
          width: 100% !important;
          height: 100% !important;
          min-width: 200px !important;
          min-height: 48px !important;
          opacity: 0.011 !important;
          cursor: pointer !important;
        }
      `}</style>
    </div>
  );
}
